import {
  CurrentUserJobStatusChannel,
  type CurrentUserJobStatusData,
} from "@/services/cableInternalV1";
import {
  FeatureFlagNameEnum,
  getAllLimitsQueryKey,
  getListItemNodesQueryKey,
  getListSharedLibraryItemNodesQueryKey,
} from "@/services/internalApiV1";
import { getCable } from "@/services/internalCableClient";
import {
  type ActionCableSubscription,
  type ActionCableJobStatusData,
} from "@/types";
import { JOB_NAMES } from "@/types/jobs";
import { decodeCablePayload } from "@/utils/cablePayload";
import { createResubscribeScheduler } from "@/utils/cableResubscribe";
import { callSafely } from "@/utils/callSafely";
import { flash } from "@/utils/flash";
import { SubscriptionRejectedError } from "@anycable/core";
import * as Sentry from "@sentry/vue";

let actionCableSubscription: ActionCableSubscription | undefined;
// ActionCable keeps no history: whatever was broadcast while the socket was
// down, or while the cable had given the subscription up, is gone. Set on
// both, cleared by the connect that tells the onReconnect listeners to catch up.
let missedBroadcastsPossible = false;
// The subscription is a module singleton, so its retry is one too. Each
// consumer instance brings its own setupSubscription — it captures the `t`
// only a setup context can obtain — and the instances are interchangeable, so
// the re-create runs whichever one set the subscription up last.
let setupSubscriptionForRetry: (() => void) | undefined;
const resubscribeScheduler = createResubscribeScheduler({
  label: "CurrentUserJobStatusChannel",
  tags: { component: "useJobStatusChannel" },
  resubscribe: () => setupSubscriptionForRetry?.(),
});

interface UseJobStatusChannelOptions {
  onUpdate: (
    data: ActionCableJobStatusData,
    payload: unknown
  ) => void | Promise<void>;
  // Called on the first connect after a socket drop or after a re-created
  // subscription. A consumer that is waiting on a job refetches here: the
  // broadcast that finished it may have been sent into the gap.
  onReconnect?: () => void | Promise<void>;
}
const onUpdateListeners: UseJobStatusChannelOptions["onUpdate"][] = [];
const onReconnectListeners: NonNullable<
  UseJobStatusChannelOptions["onReconnect"]
>[] = [];

// Actions whose `failed` broadcasts a consumer currently renders inline
// itself (e.g. the image picker's generate tab shows an error banner). The
// channel's global failed-toast would double-notify next to that, so such a
// consumer claims the action for the duration of its run. Counted, because
// two consumers of the same action can overlap (two pickers on one page).
const claimedFailureNotifications = new Map<string, number>();

export function claimJobFailureNotification(action: string): () => void {
  claimedFailureNotifications.set(
    action,
    (claimedFailureNotifications.get(action) ?? 0) + 1
  );
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const count = claimedFailureNotifications.get(action) ?? 0;
    if (count <= 1) {
      claimedFailureNotifications.delete(action);
    } else {
      claimedFailureNotifications.set(action, count - 1);
    }
  };
}

const GENERIC_EXCEPTIONS = [
  "ActiveRecord::RecordInvalid",
  "ArgumentError",
  "RuntimeError",
];

type TranslateFn = (key: string, named?: Record<string, unknown>) => string;

// Resolves the user-facing message of a `failed` job broadcast. Exported so a
// consumer that claimed the failure notification (see above) can render the
// same message inline that the channel toast would have shown.
export function resolveJobFailureMessage(
  jobStatusData: ActionCableJobStatusData,
  t: TranslateFn
): string {
  // NEW: Use pre-translated message from backend if available
  if (jobStatusData.error_message) {
    return jobStatusData.error_message;
  }

  // FALLBACK: Use client-side translation (for backwards compatibility)
  // map ruby exceptions to i18n strings
  // example: OpenAi::TimeoutError -> "Our partner OpenAI is currently experiencing too many requests. Please try again later."
  const errorMessage = t(
    `exceptionMessages.${jobStatusData?.exception ?? "default"}`,
    {
      message: jobStatusData?.message,
    }
  );

  if (
    jobStatusData?.exception &&
    GENERIC_EXCEPTIONS.includes(jobStatusData?.exception)
  ) {
    return t("errors.jobProcessingFailedWithDetails", {
      message: jobStatusData.message,
    });
  } else if (errorMessage && errorMessage.length > 0) {
    return errorMessage;
  } else {
    return jobStatusData.message ?? t("errors.jobProcessingFailed");
  }
}

// Subscribe to the CurrentUserJobStatusChannel channel and call the onUpdate
// listeners. Backed by the generated typed CurrentUserJobStatusChannel over the
// shared AnyCable cable; the public onUpdate signature is unchanged so the many
// callers keep working (they narrow on `action` themselves).
function reportListenerError(action: string, error: unknown): void {
  console.error(`[cable][sub] listener-failed: ${action}`, error);
  Sentry.captureException(error, {
    tags: { component: "useJobStatusChannel", action },
  });
}

function notifyReconnectListeners(): void {
  onReconnectListeners.forEach((listener) => {
    callSafely(listener, (error) => reportListenerError("reconnect", error));
  });
}

export function useJobStatusChannel({
  onUpdate,
  onReconnect,
}: UseJobStatusChannelOptions): {
  actionCableSubscription: ActionCableSubscription | undefined;
} {
  const { t } = useI18n();
  const userStore = useUserStore();
  const queryClient = useQueryClient();
  const adoptStore = useSharedLibraryAdoptStore();

  onUpdateListeners.push(onUpdate);
  if (onReconnect) onReconnectListeners.push(onReconnect);

  const onUpdateCallback = (data: CurrentUserJobStatusData): void => {
    // The generated union is a superset of what any single listener reads;
    // callers still discriminate on `action`, so keep the established shape.
    const jobStatusData = data as unknown as ActionCableJobStatusData;

    // A rendered jbuilder string from BroadcastingConcern, or a nested object
    // from the bulk action jobs — decodeCablePayload takes either. A payload it
    // cannot read must not escape: every consumer in the app shares this one
    // subscription, so a throw here would stop all of them over one message.
    let payload: unknown;
    try {
      payload = decodeCablePayload(jobStatusData.payload);
    } catch (error: unknown) {
      Sentry.captureException(error, {
        tags: {
          component: "useJobStatusChannel",
          action: jobStatusData.action,
        },
      });
      payload = undefined;
    }

    // Publish owns its product-specific feedback, including failures, so it
    // opts out of the generic failed-toast below. Handled once on the shared
    // subscription rather than once per mounted consumer. A flag-off event is
    // silent but still reaches the listeners.
    const isPublishFeedback =
      jobStatusData.name === JOB_NAMES.PUBLISH_ITEM_NODES &&
      userStore.isFeatureEnabled(FeatureFlagNameEnum.SHARED_LIBRARY_PUBLISH);

    // The copy exists only now, and the dialog that asked for it is long
    // unmounted, so this always-mounted singleton is where the target
    // library's listing (every page, filter and folder tree of that school)
    // is refetched. The job refreshes the search index before broadcasting,
    // so the refetch sees the copy (D5 in `12819-wire-shared-library-publish.md`).
    if (
      jobStatusData.name === JOB_NAMES.PUBLISH_ITEM_NODES &&
      jobStatusData.status === "completed"
    ) {
      const companyId = (payload as { companyId?: unknown } | undefined)
        ?.companyId;
      if (typeof companyId === "number") {
        void queryClient.invalidateQueries({
          queryKey: getListSharedLibraryItemNodesQueryKey(companyId),
        });
      }
    }

    // The other direction (#12820): a copy taken out of a Gemeinsame Ablage
    // lands in Meine Ablage, so the `item_nodes` prefix — /folders, /search and
    // the tool index pages, every place the copy can appear — is refetched.
    // The page the entry was clicked on stays as it is; nothing there changed.
    // The copy also spends a limit, so the all-limits query behind the create
    // menu (`useUserLimits`) is refetched too — the tool index counter is
    // refreshed by `ItemIndexView` itself.
    const isAdopt = jobStatusData.name === JOB_NAMES.ADOPT_ITEM_NODES;
    const isAdoptFeedback =
      isAdopt &&
      userStore.isFeatureEnabled(FeatureFlagNameEnum.SHARED_LIBRARY_PAGE);

    if (isAdopt && jobStatusData.status === "completed") {
      void queryClient.invalidateQueries({
        queryKey: getListItemNodesQueryKey(),
      });
      void queryClient.invalidateQueries({ queryKey: getAllLimitsQueryKey() });
    }

    // Either way the job went, the rows queued for copying are no longer
    // "being copied": the menus may offer the entry again (#12961 D1). The
    // broadcast names no source ids, so every queued row is released.
    if (
      isAdopt &&
      (jobStatusData.status === "completed" ||
        jobStatusData.status === "failed")
    ) {
      adoptStore.clear();
    }

    if (isPublishFeedback && jobStatusData.status === "completed") {
      // A copy never carries pupil submissions; the job says when a source had
      // any, and the toast turns into an info toast that says so (#13078). One
      // toast, not two: toasts share one fixed spot, so a second would hide
      // the first.
      const sourceHasResponses =
        (payload as { responsesNotCopied?: unknown } | undefined)
          ?.responsesNotCopied === true;
      if (sourceHasResponses) {
        flash(FlashKindEnum.INFO, t("sharedLibrary.copyResult.success"), {
          description: t("sharedLibrary.copyResult.responsesNotCopied"),
        });
      } else {
        flash(FlashKindEnum.SUCCESS, t("sharedLibrary.copyResult.success"));
      }
    } else if (isPublishFeedback && jobStatusData.status === "failed") {
      flash(FlashKindEnum.ALERT, t("sharedLibrary.copyResult.failure"), {
        description: t("sharedLibrary.copyResult.retry"),
      });
    } else if (isAdoptFeedback && jobStatusData.status === "completed") {
      flash(FlashKindEnum.SUCCESS, t("sharedLibrary.adoptResult.success"));
    } else if (isAdoptFeedback && jobStatusData.status === "failed") {
      // The one adopt that can still be refused by a limit — a member whose
      // group has no tools product — must read as words, not a Ruby class
      // (#11935 D3): the job's `error_message` is that wording, when sent.
      // A blank message is no wording either, so it falls back too.
      const wording = jobStatusData.error_message?.trim();
      flash(FlashKindEnum.ALERT, t("sharedLibrary.adoptResult.failure"), {
        description:
          wording === undefined || wording === ""
            ? t("sharedLibrary.copyResult.retry")
            : wording,
      });
    } else if (
      jobStatusData.name !== JOB_NAMES.PUBLISH_ITEM_NODES &&
      !isAdopt &&
      jobStatusData.status === "failed" &&
      !claimedFailureNotifications.has(jobStatusData.action)
    ) {
      flash(FlashKindEnum.ALERT, resolveJobFailureMessage(jobStatusData, t));
    }

    // One listener must not take down the others. Every consumer in the app
    // shares this single subscription, so an escaping throw would end job
    // status updates for the whole tab over one message a single listener
    // could not read.
    onUpdateListeners.forEach((listener) => {
      callSafely(
        () => listener(jobStatusData, payload),
        (error) => reportListenerError(jobStatusData.action, error)
      );
    });
  };

  const teardownSubscription = (): void => {
    resubscribeScheduler.cancel();
    actionCableSubscription?.unsubscribe();
    actionCableSubscription = undefined;
  };

  const handleSubscriptionChange = (): void => {
    if (userStore.isAuthenticated || userStore.isVisitor) {
      setupSubscription();
    } else {
      teardownSubscription();
    }
  };

  watch(
    () => userStore.isAuthenticated,
    () => {
      handleSubscriptionChange();
    }
  );

  watch(
    () => userStore.isVisitor,
    () => {
      handleSubscriptionChange();
    }
  );

  onMounted(() => {
    if (userStore.isAuthenticated || userStore.isVisitor) {
      setupSubscription();
    }
  });

  onUnmounted(() => {
    // remove onUpdate callback
    if (onUpdate) {
      onUpdateListeners.splice(onUpdateListeners.indexOf(onUpdate), 1);
    }
    if (onReconnect) {
      onReconnectListeners.splice(onReconnectListeners.indexOf(onReconnect), 1);
    }

    // if no more onUpdate callbacks, unsubscribe from the channel
    if (onUpdateListeners.length === 0) {
      teardownSubscription();
    }
  });

  const setupSubscription = (): void => {
    setupSubscriptionForRetry = setupSubscription;
    if (actionCableSubscription) return;
    // A consumer mounting during a retry streak subscribes right away; the
    // pending re-create would only duplicate that.
    resubscribeScheduler.cancel();

    const channel = new CurrentUserJobStatusChannel();
    getCable().subscribe(channel);
    channel.on("message", onUpdateCallback);
    channel.on("connect", () => {
      resubscribeScheduler.connected();
      if (!missedBroadcastsPossible) return;
      missedBroadcastsPossible = false;
      console.info("[cable][sub] reconnected: CurrentUserJobStatusChannel");
      notifyReconnectListeners();
    });
    channel.on("disconnect", () => {
      missedBroadcastsPossible = true;
    });
    // Our own teardown closes without an error and clears the reference
    // itself. Any other close means the cable has already dropped the
    // subscription from its hub, so the reference is stale from here on — and
    // a truthy one would keep every later setupSubscription() from subscribing.
    channel.on("close", (error) => {
      if (!error) return;
      actionCableSubscription = undefined;
      missedBroadcastsPossible = true;

      // Callers pre-translate the rejection copy to keep useI18n() out of the
      // async socket path. A rejection is the server's decision, so it is not
      // retried; the next subscribe waits for the identity to change.
      if (error instanceof SubscriptionRejectedError) {
        console.error("[cable][sub] rejected: CurrentUserJobStatusChannel");
        flash(FlashKindEnum.ALERT, t("errors.connectionRejected"));
        return;
      }

      // Every consumer in the app hears through this one subscription, so the
      // loss must neither stay silent nor stay lost: the scheduler reports it
      // and re-creates the subscription after a back-off.
      resubscribeScheduler.failed(error);
    });

    actionCableSubscription = { unsubscribe: () => channel.disconnect() };
  };

  return { actionCableSubscription };
}
